import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router'
import { ExternalLink, MessageSquare } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ErrorState } from '@/components/ui/error-state'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { SectionLoading } from './section-loading'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { ReservationSidebar } from '@/components/messages/reservation-sidebar'
import { ReservationExternalLinks } from '@/components/reservation/external-links'
import { SecurityDepositCard } from '@/components/reservation/security-deposit-card'
import { useReservation, useReservationThread } from '@/hooks/useApi'
import { formatDateRange } from '@/lib/format-utils'
import {
  CleaningStatusChip,
  cleaningState,
  isCrewMissing,
} from '@/components/calendar/cleaning-bars'
import { CLEANING_SUBSTATE_META, formatCleaningDay } from '@/components/cleaning/presentation'
import type { CleaningJob, PropertyCalendarStay } from '@/api/client'
import { DepositTimeline, type StayDeposit } from './deposit-timeline'
import { REVIEW_SECTION, ReviewSection, type StayReview } from './review-section'
import { useRangeWords } from './range-words'

/** What `?section=` can ask the stay sheet to scroll to. */
export const DEPOSIT_SECTION = 'deposits'
export { REVIEW_SECTION }

/**
 * What the calendar read says about this turnover's cleaning. `pending` is
 * "not read yet" (the calendar is still loading), shown as nothing; an
 * `error` layer is said out loud, because an unreadable layer is not "no
 * cleaning".
 */
export type TurnoverCleaning =
  | { status: 'pending' }
  | { status: 'error'; message: string }
  | {
      status: 'ok'
      job: CleaningJob | null
      crewName: string | null
      /** The crew list has not arrived: the job names a crew that cannot be named yet. */
      crewLoading?: boolean
    }

interface StaySheetProps {
  propertyId: string
  reservationId: string
  /**
   * The stay as the calendar read returned it, when it is in the visible
   * month. It names the sheet and carries the needs-reply flag while the full
   * reservation loads; a stay opened from a pasted link may not be in this
   * month at all, and the sheet works from the reservation alone.
   */
  stay: PropertyCalendarStay | null
  /** The cleaning that follows this stay, from the same calendar read. */
  cleaning: TurnoverCleaning
  /** This stay's deposit events, from the same calendar read. */
  deposit: StayDeposit
  /** This stay's review window, from the same calendar read. */
  review: StayReview
  /** The property's zone: the clock the deposit timeline's times are on. */
  timeZone: string
  /** Scroll to this part of the sheet once it has loaded (`deposits` or `reviews`). */
  section?: string | null
  /** Open that cleaning's own sheet. */
  onOpenCleaning: (jobId: string) => void
  onClose: () => void
}

/**
 * One stay, opened from the property calendar.
 *
 * Everything here is a read of what the app already shows elsewhere, in the
 * same components: the reservation summary the inbox uses, the deposit card
 * the reservation page uses (its Stripe link is the way to take or release a
 * deposit - nothing here moves money), and the same external links. The only
 * actions are links out.
 */
export function StaySheet({
  propertyId,
  reservationId,
  stay,
  cleaning,
  deposit,
  review,
  timeZone,
  section = null,
  onOpenCleaning,
  onClose,
}: StaySheetProps) {
  const reservationQuery = useReservation(reservationId)
  const threadQuery = useReservationThread(reservationId)
  const reservation = reservationQuery.data

  // `external` is `unknown` on the contract's reservation schema (a long-lived
  // blob several pipelines write), so it is narrowed the way every other reader
  // of it does.
  const sidebarReservation = reservation as Parameters<typeof ReservationSidebar>[0]['reservation']
  const uplisting = (reservation?.external as { uplisting?: { external_reservation_id?: string } })
    ?.uplisting

  const checkIn = stay?.check_in ?? reservation?.check_in ?? null
  const checkOut = stay?.check_out ?? reservation?.check_out ?? null
  const channel = stay?.channel ?? reservation?.channel ?? null
  const threadId = threadQuery.data?.thread?.threadId ?? null

  // A marker on the grid opens the sheet at the deposit or the review section.
  // Everything above it has to be on screen first - the body shows a skeleton
  // until the reservation loads, and the cleaning, review and deposit lines
  // appear when the calendar read lands - or the section would be scrolled to
  // and then pushed away. The deposit and review states settle with the same
  // calendar read as the cleaning line (a stay outside the visible month never
  // gets a cleaning line, so that one cannot be waited on). The review section
  // sits above the deposit one, so both are waited for. Once, so a later
  // refetch never pulls the operator back from reading.
  const depositRef = useRef<HTMLDivElement>(null)
  const reviewRef = useRef<HTMLElement>(null)
  const scrolledRef = useRef(false)
  const ready = !!reservation && deposit.status !== 'pending' && review.status !== 'pending'
  useEffect(() => {
    if (!ready || scrolledRef.current) return
    const target =
      section === DEPOSIT_SECTION ? depositRef : section === REVIEW_SECTION ? reviewRef : null
    if (!target) return
    scrolledRef.current = true
    target.current?.scrollIntoView?.({ block: 'start' })
  }, [section, ready])

  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent aria-describedby="stay-sheet-description" data-testid="stay-sheet">
        <SheetHeader>
          {/* The guest's name is the first thing in the body (the reservation
              summary leads with it), so the title says what this panel is
              rather than saying it twice. */}
          <SheetTitle>Stay</SheetTitle>
          <SheetDescription id="stay-sheet-description">
            {checkIn && checkOut ? formatDateRange(checkIn, checkOut) : 'Stay'}
            {stay?.nights ? ` · ${stay.nights} night${stay.nights !== 1 ? 's' : ''}` : ''}
          </SheetDescription>
          {stay?.needs_reply && (
            // Only a known "yes": the API sends null when it could not read the
            // thread, and unknown is not shown as either answer.
            <div>
              <Badge variant="warning" className="text-xs">
                Needs reply
              </Badge>
            </div>
          )}
        </SheetHeader>

        <SheetBody className="space-y-4">
          {reservationQuery.isLoading ? (
            <div className="space-y-3" aria-busy="true">
              <LoadingSkeleton className="h-6 w-2/3" />
              <LoadingSkeleton className="h-40 w-full" />
            </div>
          ) : reservationQuery.isError || !reservation ? (
            <ErrorState
              error={reservationQuery.error as Error}
              onRetry={() => void reservationQuery.refetch()}
            />
          ) : (
            <>
              <ReservationSidebar reservation={sidebarReservation} />
              <TurnoverCleaningSection cleaning={cleaning} onOpen={onOpenCleaning} />
              <ReviewSection review={review} timeZone={timeZone} sectionRef={reviewRef} />
              {/* The card (with its Stripe link) and the timeline are one
                  section: the scroll target is the top of both. */}
              <div
                ref={depositRef}
                data-testid="stay-sheet-deposit"
                className="scroll-mt-0 space-y-4"
              >
                <SecurityDepositCard reservationId={reservationId} className="md:max-w-none" />
                <DepositTimeline deposit={deposit} timeZone={timeZone} />
              </div>
              <ReservationExternalLinks
                reservationId={reservationId}
                checkIn={checkIn}
                channel={channel}
                externalReservationId={uplisting?.external_reservation_id}
              />
            </>
          )}
        </SheetBody>

        {/* One row even at 320px: on a phone the buttons drop the "Open" a
            screen reader still hears, so the footer takes one line, not two. */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border px-6 py-4 max-sm:py-3">
          <ThreadLink
            propertyId={propertyId}
            threadId={threadId}
            isLoading={threadQuery.isLoading}
            isError={threadQuery.isError}
            onRetry={() => void threadQuery.refetch()}
          />
          <Button variant="secondary" size="sm" asChild>
            <Link
              to={`/reservations/${encodeURIComponent(reservationId)}`}
              aria-label="Open reservation"
            >
              <ShortOnPhone full="Open reservation" short="Reservation" />
              {/* The arrow is the one thing that does not fit at 320px (measured:
                  276px of buttons for a 271px row); the word says where it goes. */}
              <ExternalLink className="ml-1.5 h-3.5 w-3.5 max-sm:hidden" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/** A footer button's words: the full ones, or the short ones on a phone (the link's `aria-label` keeps the full name). */
function ShortOnPhone({ full, short }: { full: string; short: string }) {
  return (
    <>
      <span className="max-sm:hidden">{full}</span>
      <span className="sm:hidden" aria-hidden="true">
        {short}
      </span>
    </>
  )
}

function ThreadLink({
  propertyId,
  threadId,
  isLoading,
  isError,
  onRetry,
}: {
  propertyId: string
  threadId: string | null
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}) {
  if (threadId) {
    return (
      <Button variant="secondary" size="sm" asChild>
        <Link
          to={`/properties/${encodeURIComponent(propertyId)}/messages/${encodeURIComponent(threadId)}`}
          aria-label="Open conversation"
        >
          <MessageSquare className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          <ShortOnPhone full="Open conversation" short="Conversation" />
        </Link>
      </Button>
    )
  }
  // A failed lookup is not "no conversation": say so and offer the retry.
  if (isError) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onRetry}
        aria-label="Couldn't check for a conversation - retry"
      >
        <ShortOnPhone full="Couldn't check for a conversation - retry" short="Retry conversation" />
      </Button>
    )
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      disabled
      title={isLoading ? undefined : 'No conversation exists yet for this reservation'}
    >
      <MessageSquare className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
      {isLoading ? 'Checking conversation' : 'No messages yet'}
    </Button>
  )
}

/**
 * The cleaning after this stay, with a way into its sheet.
 *
 * "None" is only claimed for a month that was read: the calendar asks for the
 * cleanings whose day falls in the visible month, so a stay that ends near its
 * edge can have its cleaning in the next one, and the line says "this month"
 * rather than "no cleaning".
 */
function TurnoverCleaningSection({
  cleaning,
  onOpen,
}: {
  cleaning: TurnoverCleaning
  onOpen: (jobId: string) => void
}) {
  const words = useRangeWords()
  if (cleaning.status === 'pending') return <SectionLoading label="Cleaning" />

  let content: ReactNode
  if (cleaning.status === 'error') {
    content = <p className="text-sm text-danger">{cleaning.message}</p>
  } else if (!cleaning.job) {
    content = <p className="text-sm text-textMuted">No cleaning for this stay in {words.shown}.</p>
  } else {
    const { job } = cleaning
    const day = formatCleaningDay(job.local_date, null)
    const ack =
      job.substate === 'needs_ack' || job.substate === 'ready'
        ? CLEANING_SUBSTATE_META[job.substate]
        : null
    content = (
      <Button
        variant="secondary"
        className="h-auto w-full flex-wrap justify-between gap-x-3 gap-y-1.5 py-2 text-left"
        onClick={() => onOpen(job.id)}
        data-testid="stay-sheet-cleaning-link"
      >
        {/* On a narrow sheet the badges drop below rather than run under a
            long crew name, which truncates only if it is wider than the row. */}
        <span className="min-w-0 max-w-full flex-auto">
          <span className="block whitespace-nowrap text-sm font-medium tabular-nums">{day}</span>
          {cleaning.crewName && (
            <span className="block truncate text-xs font-normal text-textMuted">
              {cleaning.crewName}
            </span>
          )}
          {cleaning.crewLoading && (
            <span
              className="block h-4 py-1"
              data-testid="stay-sheet-crew-loading"
              role="status"
              aria-label="Crew loading"
            >
              <span
                aria-hidden="true"
                className="block h-2 w-16 animate-pulse rounded-sm bg-current opacity-20 motion-reduce:animate-none"
              />
            </span>
          )}
        </span>
        <span className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          <CleaningStatusChip
            state={cleaningState(job)}
            noCrew={isCrewMissing(job)}
            testId="stay-sheet-cleaning-status"
          />
          {/* Whether the crew has acked is not a state the bar says (a confirmed
              job is Scheduled either way), but the row used to, and "Scheduled"
              alone must not read as "the crew is coming" when they have not
              said so. */}
          {ack && (
            <Badge
              variant={ack.tone as 'warning' | 'success'}
              data-testid="stay-sheet-cleaning-ack"
            >
              {ack.label}
            </Badge>
          )}
        </span>
      </Button>
    )
  }

  return (
    <section aria-label="Cleaning" className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">Cleaning</h3>
      {content}
    </section>
  )
}
